import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { getAuth, signOut } from 'firebase/auth'
import { Button } from 'storybook-ui'

import { Container } from 'components/Container'
import { FooterContainer } from 'components/FooterContainer'
import { RadiantSpinner } from 'components/RadiantSpinner'

import {
  UNSUBSCRIBE,
  getUserSubscriptionsInfoUnifiedAction,
} from 'root-redux/actions/user'
import { selectActionList, selectAppName } from 'root-redux/selects/common'
import { TAppDispatch } from 'root-redux/store/store'

import { usePlanUpdateFakeDelay } from 'hooks/usePlanUpdateFakeDelay'

import { PinnedNote } from 'modules/unsubscribe/components/PinnedNote'
import { RetentionPageContainer } from 'modules/unsubscribe/components/RetentionPageContainer'

import { ICustomRouterState } from 'models/common.model'

import { goTo } from 'browser-history'
import { RetentionPageTitle as Title } from 'common-styles'
import { PageId } from 'page-constants'
import { APP_BUTTON_THEMES, SubscriptionDetailsTab } from 'root-constants'

export const CanceledPlan: React.FC = () => {
  const { search, state } = useLocation()
  const dispatch: TAppDispatch = useDispatch()
  const { t } = useTranslation()
  const appName = useSelector(selectAppName)
  const fetchingActionsList = useSelector(selectActionList)
  const isReadyToNextStep = usePlanUpdateFakeDelay()

  const isUnsubscribeInProgress = fetchingActionsList.includes(UNSUBSCRIBE)
  const isWithdrawal = (state as ICustomRouterState)?.isWithdrawal

  const signOutFirebase = useCallback(() => signOut(getAuth()), [])

  const goToAccountPage = async () => {
    await dispatch(getUserSubscriptionsInfoUnifiedAction(signOutFirebase))

    goTo({
      pathname: PageId.SUBSCRIPTION_DETAILS,
      search,
    })
  }

  useEffect(() => {
    if (isUnsubscribeInProgress || !isReadyToNextStep || !isWithdrawal) return

    goTo(
      {
        pathname: PageId.SUBSCRIPTION_DETAILS,
        search,
      },
      {
        defaultTab: SubscriptionDetailsTab.SUBSCRIPTIONS,
      },
    )
  }, [isUnsubscribeInProgress, isReadyToNextStep, isWithdrawal, search])

  return (
    <RetentionPageContainer>
      {isUnsubscribeInProgress || !isReadyToNextStep || isWithdrawal ? (
        <Container>
          <Title bottomMargin={48}>
            {t`retention.canceledPlan.step1Title`}
          </Title>
          <RadiantSpinner />
        </Container>
      ) : (
        <>
          <Container>
            <Title bottomMargin={48}>
              {t`retention.canceledPlan.step2Title`}
            </Title>
            <PinnedNote>{t`retention.canceledPlan.note`}</PinnedNote>
          </Container>
          <FooterContainer>
            <Button
              theme={APP_BUTTON_THEMES[appName]}
              onClick={goToAccountPage}
            >
              {t('retention.canceledPlan.okButton', { context: appName })}
            </Button>
          </FooterContainer>
        </>
      )}
    </RetentionPageContainer>
  )
}
